import { BAOCustomElement } from '@bao/custom-element'
import { MediaBreakpoints } from '@bao/media'
import { debounce, getElement, getOffsetLeft, getOffsetTop } from '@bao/utils'

class TimelineScroll extends BAOCustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.isTicking = false
    this.isDatesTicking = false
    this.datePositions = []
    this.lastKnownY = window.scrollY
    this._progress = 0

    this.requestTick = this.requestTick.bind(this)
    this.updateEl = this.updateEl.bind(this)
    this.updateActiveDate = this.updateActiveDate.bind(this)
    this.setupSizes = this.setupSizes.bind(this)

    this.getDatePositions()

    this.els.item.elements.forEach((itemEl) => {
      const observer = new IntersectionObserver(this.onEntry.bind(this), {
        rootMargin: '0px 0px 0px 0px',
        threshold: 0.6,
      })

      observer.observe(itemEl)
    })

    this.setupSizes()
    this.updateEl()
  }

  setupListeners () {
    super.setupListeners()

    this.onScroll = this.onScroll.bind(this)
    window.addEventListener('scroll', this.onScroll, { passive: true })

    this.debouncedOnResize = debounce(() => {
      this.setupSizes()
      this.updateDates()
    }, 200)

    window.addEventListener('resize', this.debouncedOnResize)

    this.els.date.elements.forEach((dateEl) => {
      this.listeners.add(dateEl, 'click', this.handleDateClick.bind(this))
    })
  }

  getDatePositions () {
    const containerLeft =
      this.els.datesScroll.element.getBoundingClientRect().left

    this.datePositions = this.els.date.elements.map((dateEl) => {
      return dateEl.getBoundingClientRect().left - containerLeft
    })
  }

  onEntry ([entry]) {
    if (!entry.isIntersecting) return

    const { target } = entry
    if (!target) return

    target.dataset.seen = true
    const id = target.dataset.id

    /* Enable update desktop attributes based on active entry */
    this.dataset.showLines = target.dataset.hasLines === 'true'

    this.style.setProperty(
      '--Desktop_Background',
      target.dataset.backgroundColour,
    )

    /* Animate title */
    const title = getElement('animate-in', { context: target })

    if (title) {
      title.setAttribute('animate', '')
    }

    /* Update active date and scroll */
    this.updateDates(id)
  }

  onScroll () {
    this.lastKnownY = window.scrollY

    this.requestTick()
  }

  requestTick () {
    if (!this.isTicking) requestAnimationFrame(this.updateEl)

    this.isTicking = true
  }

  updateEl () {
    this.isTicking = false

    this.moveTimeline()
  }

  setupSizes () {
    if (!MediaBreakpoints.isMd) return

    /* Set container height */
    const roundedHeight = Math.round(this.mediaViewportPercent)

    this.style.setProperty(
      '--Media_Total',
      `${roundedHeight > 100 ? roundedHeight : 110}vh`,
    )

    /* Position date bar */
    const firstElCenter = this.els.date.elements[0].offsetWidth / 2
    const start = 30 + firstElCenter

    const lastElCenter =
      this.els.date.elements[this.els.date.elements.length - 1].offsetWidth / 2
    const end = 30 + lastElCenter

    this.els.bar.element.style.insetInlineStart = `${start}px`
    this.els.bar.element.style.inlineSize = `calc(100% - ${start}px - ${end}px)`
  }

  updateDates (id) {
    if (!MediaBreakpoints.isMd) return

    this.els.item.elements.forEach((itemEl) => {
      const isActive = itemEl.dataset.id === id

      itemEl.dataset.active = isActive
    })

    this.els.date.elements.forEach((dateEl, index) => {
      const isActive = id
        ? dateEl.dataset.id === id
        : dateEl.dataset.active === 'true'

      if (id) {
        dateEl.dataset.active = isActive
      }
    })

    this.requestDatesTick()
  }

  requestDatesTick () {
    if (!this.isDatesTicking) requestAnimationFrame(this.updateActiveDate)

    this.isDatesTicking = true
  }

  updateActiveDate () {
    const datesContainer = this.els.datesScroll.element

    this.els.date.elements.forEach((dateEl, index) => {
      const isActive = dateEl.dataset.active === 'true'

      if (!isActive) return

      const containerWidth = datesContainer.offsetWidth
      const elementWidth = dateEl.offsetWidth + 40
      const offset = (containerWidth - elementWidth) / 2

      const leftPadding = index ? 20 : 30
      const left = this.datePositions[index] - leftPadding - offset

      datesContainer.scroll({
        behavior: this.prefersReducedMotion ? 'instant' : 'smooth',
        left,
        top: 0,
      })
    })

    this.isDatesTicking = false
  }

  moveTimeline () {
    if (!MediaBreakpoints.isMd) return

    const maxScrollAmount = this.mediaViewportPercent - 100

    const elementHeight = this.offsetHeight

    const elementStartPoint = getOffsetTop(this)
    const elementEndPoint =
      getOffsetTop(this) + elementHeight - window.innerHeight

    if (this.lastKnownY <= elementStartPoint) {
      this.style.setProperty('--Scroll_Amount', `0vw`)
      this.progress = 0

      return
    }

    if (this.lastKnownY > elementEndPoint) {
      this.style.setProperty('--Scroll_Amount', `-${maxScrollAmount}vw`)
      this.progress = 1

      return
    }

    const totalScrollAmount = elementEndPoint - elementStartPoint

    const scrollYAsAPercentageOfJourney =
      (this.lastKnownY - elementStartPoint) / totalScrollAmount

    const scrollAmount = maxScrollAmount * scrollYAsAPercentageOfJourney

    this.style.setProperty('--Scroll_Amount', `-${scrollAmount}vw`)
    this.progress = scrollYAsAPercentageOfJourney
  }

  handleDateClick (event) {
    event.preventDefault()

    const { target } = event
    const id = target.dataset.id

    const matchedItemEl = this.els.item.elements.find((itemEl) => {
      return itemEl.dataset.id === id
    })

    if (!matchedItemEl) return

    const index = matchedItemEl.dataset.index

    const elementHeight = this.offsetHeight
    const elementStartPoint = getOffsetTop(this)
    const elementEndPoint = elementStartPoint + elementHeight
    const totalScrollAmount = elementEndPoint - elementStartPoint

    const itemHeight = totalScrollAmount / this.itemCount

    window.scroll({
      behavior: this.prefersReducedMotion ? 'instant' : 'smooth',
      left: 0,
      top: itemHeight * index + elementStartPoint,
    })
  }

  get itemCount () {
    return Number(this.dataset.itemCount)
  }

  get mediaViewportPercent () {
    const width = this.els.item.elements.reduce((accumulator, current) => {
      return accumulator + current.offsetWidth
    }, 0)

    return (width / window.innerWidth) * 100
  }

  get prefersReducedMotion () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  get progress () {
    return this._progress
  }

  set progress (value) {
    this._progress = value
    const itemsViewed = this.progress / this.itemWidthPercent
    const index = Math.min(Math.floor(itemsViewed), this.itemCount - 1)
    const remaining = itemsViewed - index

    const currentDatePercentWidth = this.datePercentWidths[index]
    const nextDatePercentWidth = this.datePercentWidths[index + 1]

    let dateProgress = currentDatePercentWidth

    if (nextDatePercentWidth) {
      const difference = nextDatePercentWidth - currentDatePercentWidth
      dateProgress += difference * remaining
    }

    this.style.setProperty('--Date_Progress', `${dateProgress * 100}%`)
  }

  get datePercentWidths () {
    const barWidth = this.els.bar.element.offsetWidth
    const barOffsetLeft = getOffsetLeft(
      this.els.bar.element,
      this.els.dates.element,
    )

    return this.els.date.elements.map((dateEl, index) => {
      if (!index) {
        return 0
      }

      if (index === this.els.date.elements.length - 1) {
        return 1
      }

      const offsetLeft =
        getOffsetLeft(dateEl, this.els.dates.element) - barOffsetLeft
      const elementCenter = dateEl.offsetWidth / 2
      const percentOfBarWidth = (offsetLeft + elementCenter) / barWidth

      return percentOfBarWidth
    })
  }

  get itemWidthPercent () {
    return 1 / (this.itemCount - 1)
  }
}

if (!customElements.get('timeline-scroll')) {
  customElements.define('timeline-scroll', TimelineScroll)
}
